import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import {
  Eye,
  User,
  PaperPlaneTilt,
  Calendar,
  CalendarPlus,
  CalendarCheck,
  CalendarX,
  Trash,
  FileText,
  XCircle,
  Info,
  Handshake,
} from "@phosphor-icons/react";
import { CandidateMailStatusCell } from "../CandidateMailStatusCell";
import FollowUpCellDisplay from "../follow-up/FollowUpCellDisplay";
import { TwilioCallButton } from "shared/components/TwilioCallButton/TwilioCallButton";
import { DevWhatsAppButton } from "shared/components/TwilioCallButton/DevWhatsAppButton";
import { format } from "date-fns";
import StatusTooltip from "../../../screens/buildings/components/statusTab/StatusTooltip";
import { createTooltipContent } from "components/card/buildingCard";
import type { CellConfig } from "shared/components/UniversalTable/components";
import type { CandidateInfo, UnitInfoEntry } from "../types";
import { statusTranslation, labelTranslator } from "../types";
import {
  Tooltip as ShadTooltip,
  TooltipTrigger as ShadTooltipTrigger,
  TooltipContent as ShadTooltipContent,
  TooltipProvider as ShadTooltipProvider,
} from "@/components/ui/tooltip";

// ========== CONTACT PICTURE ==========

interface ContactPictureProps {
  pictureUrl: string;
  altName: string;
}

const ContactPicture = ({ pictureUrl, altName }: ContactPictureProps) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <User size={12} weight="fill" />;
  }

  return (
    <img
      src={pictureUrl}
      alt={altName}
      className="h-full w-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

// ========== HANDLER INTERFACE ==========

export interface CandidateCellHandlers {
  onToggleSelect: (selected: boolean) => void;
  onOpenDetail: (candidate: CandidateInfo) => void;
  onManageCandidate: (candidate: CandidateInfo) => void;
  onDocumentPreview: (residentId: string, docTypes: string[], title: string) => void;
  onSelectAlternateDate: (candidate: CandidateInfo) => void;
  onDisapproveAlternates: (candidate: CandidateInfo) => void;
  onOpenManualAppointment: (candidate: CandidateInfo) => void;
  onDeleteArchive: (candidate: CandidateInfo) => void;
  onFollowUpClick: (candidate: CandidateInfo) => void;
  onLogPhoneCall: (candidate: CandidateInfo, phoneNumber: string, durationSeconds?: number) => void;
}

export interface RenderRowContext {
  isSelected: boolean;
  isBulkActive: boolean;
  templateColumns: string;
  onToggleSelect: (selected: boolean) => void;
  onClearSelection: () => void;
}

export interface CandidateCellFactoryParams {
  candidate: CandidateInfo;
  context: RenderRowContext;
  handlers: CandidateCellHandlers;
  unitInfo?: Map<string, UnitInfoEntry>;
}

// ========== VACANCY CONFIG ==========

const vacancyConfig: Record<string, { label: string; classes: string }> = {
  vacant: { label: "Leeg", classes: "bg-moss-primary/10 border border-moss-primary text-moss-primary" },
  upcoming: { label: "In opzeg", classes: "bg-amber-50 border border-amber-400 text-amber-700" },
  renovation: { label: "Renovatie", classes: "bg-blue-50 border border-blue-300 text-blue-700" },
  concept: { label: "Concept", classes: "bg-purple-50 border border-purple-300 text-purple-700" },
  rented: { label: "Verhuurd", classes: "bg-red-50 border border-red-300 text-red-700" },
};

// ========== CELL FACTORY ==========

export function createCandidateCells({
  candidate,
  context,
  handlers,
  unitInfo,
}: CandidateCellFactoryParams): CellConfig[] {
  const [status, Icon, statusClass, borderClass] = statusTranslation(candidate.status);

  // Build agreement labels for the StatusTooltip
  const agreementLabels: Record<string, string[]> = {
    green: [],
    orange: [],
    red: [],
    transparent: [],
  };

  for (const check of candidate.checklist) {
    const good = check.isChecked !== labelTranslator(check.label).swapStatus;
    const labelField = good ? agreementLabels.green : agreementLabels.red;
    labelField.push(
      `${good ? labelTranslator(check.label)?.onGood || "" : labelTranslator(check.label)?.onBad || ""} ${labelTranslator(check.label).label}`,
    );
  }

  const modifiedLabels = Object.fromEntries(
    Object.entries(agreementLabels).map(([color, labels]) => [
      color,
      {
        sources: labels.map((label: string) => ({
          label: "Akkoord met:" + label,
          isUnit: true,
          name: candidate.unitId,
        })),
      },
    ]),
  );

  const modifiedLabelsWithTooltip = Object.fromEntries(
    Object.entries(modifiedLabels).map(([color, data]) => [
      color,
      {
        ...data,
        tooltipContent: createTooltipContent(data.sources),
      },
    ]),
  );

  // Unit info
  const info = unitInfo?.get(candidate.unitId);
  const vacancyStatus = info?.status || "rented";
  const slotCount = info?.availableTimeslots ?? 0;
  const slotDetails = info?.timeslotDetails ?? [];
  const visitContact = info?.visitationContact ?? null;
  const brokerContacts = info?.brokerContacts ?? [];
  const hasBrokers = brokerContacts.length > 0;
  const vc = vacancyConfig[vacancyStatus] || vacancyConfig.rented;

  // Document holders
  const incomeDocumentHolder = candidate.residents?.find((r) => r.incomeMonth1);
  const identityDocumentHolder = candidate.residents?.find((r) => r.idFront || r.idBack);

  return [
    // Checkbox
    {
      id: "checkbox",
      colSpan: 1,
      align: "center",
      content: (
        <Checkbox
          checked={context.isSelected}
          onCheckedChange={(checked) => {
            context.onToggleSelect(checked === true);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },

    // Unit
    {
      id: "unit",
      colSpan: 1,
      align: "left",
      content: (
        <div className="flex items-center gap-1.5">
          <a
            href={`/unit/detail/${candidate.unitId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {candidate.unitId}
          </a>
          {hasBrokers && (
            <ShadTooltipProvider>
              <ShadTooltip>
                <ShadTooltipTrigger asChild>
                  <div
                    className="flex h-5 min-w-5 items-center justify-center gap-0.5 rounded-full border border-amber-400 bg-amber-50 px-1 text-amber-600 shrink-0"
                    aria-label={`${brokerContacts.length} makelaar(s) toegewezen`}
                  >
                    <Handshake size={12} weight="fill" />
                    {brokerContacts.length > 1 && (
                      <span className="text-[10px] font-semibold leading-none">
                        {brokerContacts.length}
                      </span>
                    )}
                  </div>
                </ShadTooltipTrigger>
                <ShadTooltipContent>
                  <div className="space-y-0.5 text-xs">
                    <p className="font-semibold">
                      {brokerContacts.length > 1
                        ? `${brokerContacts.length} makelaars`
                        : "Makelaar"}
                      :
                    </p>
                    {brokerContacts.map((b) => (
                      <p key={b.id ?? `${b.firstName}-${b.lastName}`}>
                        {`${b.firstName || ""} ${b.lastName || ""}`.trim() || "Toegewezen"}
                        {b.company?.name ? ` · ${b.company.name}` : ""}
                      </p>
                    ))}
                  </div>
                </ShadTooltipContent>
              </ShadTooltip>
            </ShadTooltipProvider>
          )}
        </div>
      ),
    },

    // Vacancy
    {
      id: "vacancy",
      colSpan: 1,
      align: "center",
      content: (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${vc.classes}`}>
          {vc.label}
        </span>
      ),
    },

    // Calendar
    {
      id: "calendar",
      colSpan: 1,
      align: "center",
      content: (
        <div className="flex items-center gap-1.5">
          <ShadTooltipProvider>
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <a
                  href={`/unit/detail/${candidate.unitId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`flex items-center gap-1 rounded-full px-2 py-1 border transition-colors cursor-pointer ${
                    slotCount > 0
                      ? "border-moss-primary bg-moss-primary/5 text-moss-primary hover:bg-moss-primary/10"
                      : "border-red-300 bg-red-50 text-red-500 hover:bg-red-100"
                  }`}
                >
                  {slotCount > 0 ? (
                    <CalendarCheck size={14} weight="duotone" />
                  ) : (
                    <CalendarX size={14} weight="duotone" />
                  )}
                  <span className="text-xs font-semibold">{slotCount}</span>
                </a>
              </ShadTooltipTrigger>
              <ShadTooltipContent side="top" className="max-w-xs">
                {slotDetails.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold">
                      {slotCount} tijdslot{slotCount !== 1 ? "en" : ""}
                    </p>
                    {slotDetails.slice(0, 5).map((slot) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      return (
                        <div key={slot.id} className="flex items-center justify-between gap-3 text-xs">
                          <span>
                            {start.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit" })}{" "}
                            {start.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                            –{end.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          <span
                            className={`font-medium ${
                              slot.bookingCount === slot.maxCapacity
                                ? "text-red-600"
                                : slot.bookingCount > 0
                                  ? "text-amber-600"
                                  : "text-green-600"
                            }`}
                          >
                            {slot.bookingCount}/{slot.maxCapacity}
                          </span>
                        </div>
                      );
                    })}
                    {slotDetails.length > 5 && (
                      <p className="text-xs text-neutral-400">+{slotDetails.length - 5} meer…</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs">Geen tijdsloten — klik om unit te openen</p>
                )}
              </ShadTooltipContent>
            </ShadTooltip>
          </ShadTooltipProvider>

          <ShadTooltipProvider>
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full border overflow-hidden ${
                    visitContact
                      ? "border-moss-primary bg-moss-primary/5 text-moss-primary"
                      : "border-red-300 bg-red-50 text-red-400"
                  }`}
                >
                  {visitContact?.picture ? (
                    <ContactPicture
                      pictureUrl={visitContact.picture}
                      altName={`${visitContact.firstName || ""} ${visitContact.lastName || ""}`.trim()}
                    />
                  ) : (
                    <User size={12} weight={visitContact ? "fill" : "regular"} />
                  )}
                </div>
              </ShadTooltipTrigger>
              <ShadTooltipContent>
                {visitContact ? (
                  <p className="text-xs">
                    {`${visitContact.firstName || ""} ${visitContact.lastName || ""}`.trim()}
                  </p>
                ) : (
                  <p className="text-xs">Geen bezichtigingsverantwoordelijke</p>
                )}
              </ShadTooltipContent>
            </ShadTooltip>
          </ShadTooltipProvider>
        </div>
      ),
    },

    // Status
    {
      id: "status",
      colSpan: 1,
      align: "left",
      content: (
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${borderClass} ${statusClass} bg-white`}
            >
              <Icon className={`h-3.5 w-3.5 ${statusClass}`} />
              {status}
            </span>
            {candidate.status === "invited" && candidate.invitationSentAt && (
              <span className="text-xs text-[var(--text-secondary)] pl-1">
                Laatste contact: {format(new Date(candidate.invitationSentAt), "dd/MM/yyyy")}
              </span>
            )}
            {candidate.status === "reminder-sent" && candidate.reminderSentAt && (
              <span className="text-xs text-[var(--text-secondary)] pl-1">
                Laatste contact: {format(new Date(candidate.reminderSentAt), "dd/MM/yyyy")}
              </span>
            )}
          </div>
          {candidate.status === "searchingAppointmentMoment" && (
            <ShadTooltipProvider>
              <ShadTooltip>
                <ShadTooltipTrigger asChild>
                  <div className="cursor-help">
                    <Info
                      size={16}
                      weight="duotone"
                      className="text-neutral-500 transition-colors hover:text-neutral-700"
                    />
                  </div>
                </ShadTooltipTrigger>
                <ShadTooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">
                    Deze persoon had alternatieve data voorgesteld dewelke door Yally werden
                    afgewezen. Er moet nu een nieuw bezichtigingsmoment met deze persoon gepland
                    worden door met deze persoon te bellen of te mailen. Daarna mag via de knop
                    &quot;Plan afspraak in&quot; de afspraak worden bevestigd.
                  </p>
                </ShadTooltipContent>
              </ShadTooltip>
            </ShadTooltipProvider>
          )}
        </div>
      ),
    },

    // Action
    {
      id: "action",
      colSpan: 1,
      align: "center",
      content: (
        <div className="flex items-center gap-3">
          <ShadTooltipProvider>
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.onManageCandidate(candidate);
                  }}
                  className="hover:opacity-80"
                >
                  <PaperPlaneTilt className="h-[20px] w-[20px] text-blue-600" />
                </button>
              </ShadTooltipTrigger>
              <ShadTooltipContent>E-mail beheren</ShadTooltipContent>
            </ShadTooltip>
          </ShadTooltipProvider>

          <ShadTooltipProvider>
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.onDeleteArchive(candidate);
                  }}
                  className="hover:opacity-80"
                >
                  <Trash className="h-[20px] w-[20px] text-red-600" />
                </button>
              </ShadTooltipTrigger>
              <ShadTooltipContent>Verwijderen</ShadTooltipContent>
            </ShadTooltip>
          </ShadTooltipProvider>

          <ShadTooltipProvider>
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.onOpenDetail(candidate);
                  }}
                  className="hover:opacity-80"
                >
                  <Eye className="text-moss-primary h-[20px] w-[20px]" />
                </button>
              </ShadTooltipTrigger>
              <ShadTooltipContent>Meer details</ShadTooltipContent>
            </ShadTooltip>
          </ShadTooltipProvider>
        </div>
      ),
    },

    // Person (name + phone/whatsapp)
    {
      id: "person",
      colSpan: 1,
      align: "left",
      content: (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full border border-moss-primary bg-white px-2 py-0.5 text-xs font-medium text-moss-primary truncate">
            <User className="text-moss-primary h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{candidate.personalDetails.firstName} {candidate.personalDetails.lastName}</span>
          </span>
          {candidate.personalDetails.phoneNumber && (
            <div className="flex items-center gap-0.5 shrink-0">
              <TwilioCallButton
                phoneNumber={candidate.personalDetails.phoneNumber}
                contactName={`${candidate.personalDetails.firstName} ${candidate.personalDetails.lastName}`}
                size={14}
                onCallCompleted={(phoneNumber, durationSeconds) => {
                  handlers.onLogPhoneCall(candidate, phoneNumber, durationSeconds);
                }}
              />
              <DevWhatsAppButton
                phoneNumber={candidate.personalDetails.phoneNumber}
                contactName={`${candidate.personalDetails.firstName} ${candidate.personalDetails.lastName}`}
                size={14}
              />
            </div>
          )}
        </div>
      ),
    },

    // Follow-up (stars + action count + edit)
    {
      id: "follow-up",
      colSpan: 1,
      align: "left",
      content: (
        <FollowUpCellDisplay
          summary={candidate.followUpSummary ?? null}
          onClick={() => handlers.onFollowUpClick(candidate)}
          onQuickCall={() => handlers.onFollowUpClick(candidate)}
        />
      ),
    },

    // Candidacy date
    {
      id: "candidacy-date",
      colSpan: 1,
      align: "left",
      content: (
        <span className="text-sm text-neutral-primary">
          {candidate.createDate && !isNaN(candidate.createDate.getTime())
            ? format(candidate.createDate, "dd/MM/yyyy HH:mm")
            : "-"}
        </span>
      ),
    },

    // Visit date / actions
    {
      id: "visit",
      colSpan: 1,
      align: "left",
      content:
        candidate.status === "awaiting-alternate-selection" ? (
          <div className="flex flex-col gap-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.onSelectAlternateDate(candidate);
              }}
              className="flex items-center gap-1 rounded-md bg-gradient-to-r from-moss-primary to-green-600 px-2 py-1 text-xs font-medium text-white shadow-sm transition-all hover:from-green-600 hover:to-green-700"
            >
              <Calendar size={14} />
              Datum selecteren
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.onDisapproveAlternates(candidate);
              }}
              className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
            >
              <XCircle size={14} />
              Afwijzen
            </button>
          </div>
        ) : candidate.status === "searchingAppointmentMoment" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onOpenManualAppointment(candidate);
            }}
            className="flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
          >
            <CalendarPlus size={14} />
            Inplannen
          </button>
        ) : candidate.visitDate && !isNaN(candidate.visitDate.getTime()) ? (
          <span className="text-sm text-neutral-primary">
            {format(candidate.visitDate, "dd/MM/yyyy HH:mm")}
          </span>
        ) : (
          <span className="text-sm text-neutral-400">-</span>
        ),
    },

    // Residents count
    {
      id: "residents",
      colSpan: 1,
      align: "center",
      content: (
        <span className="text-sm text-neutral-primary">
          {(candidate.numberOfAdults || 0) + (candidate.numberOfChildren || 0) || "-"}
        </span>
      ),
    },

    // Documents
    {
      id: "docs",
      colSpan: 1,
      align: "center",
      content:
        incomeDocumentHolder || identityDocumentHolder ? (
          <div className="flex items-center gap-3">
            {incomeDocumentHolder && (
              <ShadTooltipProvider>
                <ShadTooltip>
                  <ShadTooltipTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlers.onDocumentPreview(
                          incomeDocumentHolder.id,
                          ["incomeMonth"],
                          `Loonfiche ${incomeDocumentHolder.firstName || ""} ${incomeDocumentHolder.lastName || ""}`.trim(),
                        );
                      }}
                      className="border-moss-primary/40 text-moss-primary hover:bg-moss-primary flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:text-white"
                      aria-label="Bekijk loonfiche"
                      type="button"
                    >
                      <FileText size={18} weight="duotone" />
                    </button>
                  </ShadTooltipTrigger>
                  <ShadTooltipContent>
                    <p>Bekijk loonfiche</p>
                  </ShadTooltipContent>
                </ShadTooltip>
              </ShadTooltipProvider>
            )}
            {identityDocumentHolder && (
              <ShadTooltipProvider>
                <ShadTooltip>
                  <ShadTooltipTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlers.onDocumentPreview(
                          identityDocumentHolder.id,
                          ["idFront", "idBack"],
                          `Identiteitskaart ${identityDocumentHolder.firstName || ""} ${identityDocumentHolder.lastName || ""}`.trim(),
                        );
                      }}
                      className="border-moss-primary/40 text-moss-primary hover:bg-moss-primary flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:text-white"
                      aria-label="Bekijk identiteitskaart"
                      type="button"
                    >
                      <FileText size={18} weight="duotone" />
                    </button>
                  </ShadTooltipTrigger>
                  <ShadTooltipContent>
                    <p>Bekijk identiteitskaart</p>
                  </ShadTooltipContent>
                </ShadTooltip>
              </ShadTooltipProvider>
            )}
          </div>
        ) : (
          <span className="text-sm text-neutral-400">-</span>
        ),
    },

    // Agreement (checklist status tooltips)
    {
      id: "agreement",
      colSpan: 1,
      align: "left",
      content: (
        <div className="flex cursor-pointer flex-wrap items-center gap-2">
          <StatusTooltip
            numericValues={modifiedLabelsWithTooltip}
            subTitle={candidate.unitId}
            color="green"
          />
          <StatusTooltip
            numericValues={modifiedLabelsWithTooltip}
            subTitle={candidate.unitId}
            color="orange"
          />
          <StatusTooltip
            numericValues={modifiedLabelsWithTooltip}
            subTitle={candidate.unitId}
            color="red"
          />
          <StatusTooltip
            numericValues={modifiedLabelsWithTooltip}
            subTitle={candidate.unitId}
            color="transparent"
          />
        </div>
      ),
    },

    // Mail schedule status
    {
      id: "mails",
      colSpan: 1,
      align: "center",
      content: (
        <CandidateMailStatusCell schedules={candidate.emailSchedules ?? []} />
      ),
    },
  ];
}
